import type { SectionJson, SectionsExportInfo, SectionsExportJson } from '@/api/types/core/sectionsJson';
import type { Translation } from '@/api/types/core/translations';
import { useAuthStore } from '@/stores/auth';
import type { SectionExt } from '@/stores/sectionsStore';
import { useSectionsStore } from '@/stores/sectionsStore';
import { ref, type Ref } from 'vue';

export const useSectionsExport = (): {
  exporting: Ref<boolean>;
  exportError: Ref<string | null>;
  exportSections: () => Promise<SectionsExportJson | null>;
  downloadSectionsExport: () => Promise<void>;
  validateExportData: () => { isValid: boolean; errors: string[] };
  convertSectionToJson: (section: SectionExt) => SectionJson;
  generateExportInfo: (sections: SectionExt[]) => SectionsExportInfo;
} => {
  const sectionsStore = useSectionsStore();
  const authStore = useAuthStore();
  
  const exporting = ref(false);
  const exportError = ref<string | null>(null);

  /**
   * Convertit une section avec ses données en format JSON d'export
   */
  const convertSectionToJson = (section: SectionExt): SectionJson => {
    const sectionJson: SectionJson = {
      id: section.name
    };

    // Ajouter les propriétés optionnelles seulement si elles diffèrent des valeurs par défaut
    if (section.type && section.type.trim() !== '') {
      sectionJson.type = section.type;
    }

    if (section.display === false) {
      sectionJson.display = false;
    }

    if (section.mandatory_entry === true) {
      sectionJson.mandatory_entry = true;
    }

    if (section.ordre !== undefined && section.ordre !== 0) {
      sectionJson.ordre = section.ordre;
    }

    if (section.importance !== undefined && section.importance !== 0) {
      sectionJson.importance = section.importance;
    }

    // Ajouter les traductions si elles existent
    if (section.translations && section.translations.length > 0) {
      sectionJson.translations = {};
      section.translations.forEach((translation: Translation) => {
        if (translation.language_code && sectionJson.translations) {
          sectionJson.translations[translation.language_code] = {
            titre: translation.titre || '',
            description: translation.description || undefined
          };
        }
      });
    }

    // Ajouter la configuration de champ si elle existe
    if (section.fieldConfigs && section.fieldConfigs.length > 0) {
      const fieldConfig = section.fieldConfigs[0]; // Prendre la première config
      if (fieldConfig.condition_id && fieldConfig.condition_value) {
        // Convertir l'ID de condition en nom de section
        const conditionSection = sectionsStore.sections.find(s => s.id === fieldConfig.condition_id);
        if (conditionSection) {
          sectionJson.field_config = {
            condition_id: conditionSection.name,
            condition_value: fieldConfig.condition_value
          };
        }
      }
    }

    return sectionJson;
  };

  /**
   * Génère les informations d'export
   */
  const generateExportInfo = (sections: SectionExt[]): SectionsExportInfo => {
    const totalTranslations = sections.reduce((count, section) => {
      return count + (section.translations?.length || 0);
    }, 0);

    const totalFieldConfigs = sections.reduce((count, section) => {
      return count + (section.fieldConfigs?.length || 0);
    }, 0);

    return {
      version: '1.0',
      timestamp: new Date().toISOString(),
      total_sections: sections.length,
      total_translations: totalTranslations,
      total_field_configs: totalFieldConfigs,
      exported_by: authStore.user?.email || 'unknown',
      export_type: 'sections'
    };
  };

  /**
   * Exporte toutes les sections en format JSON
   */
  const exportSections = async (): Promise<SectionsExportJson | null> => {
    exporting.value = true;
    exportError.value = null;

    try {
      // S'assurer que les données sont à jour
      await sectionsStore.fetchSectionsData();

      const sections = sectionsStore.sections;
      if (sections.length === 0) {
        throw new Error('Aucune section à exporter');
      }

      // Convertir toutes les sections en format JSON
      const sectionsJson: SectionJson[] = sections.map(convertSectionToJson);

      // Générer les informations d'export
      const exportInfo = generateExportInfo(sections);

      // Créer l'objet d'export complet
      const exportData: SectionsExportJson = {
        export_info: exportInfo,
        sections: sectionsJson
      };

      return exportData;

    } catch (error) {
      console.error('❌ Erreur lors de l\'export des sections:', error);
      exportError.value = error instanceof Error ? error.message : 'Erreur inconnue lors de l\'export';
      return null;
    } finally {
      exporting.value = false;
    }
  };

  /**
   * Télécharge les sections exportées sous forme de fichier JSON
   */
  const downloadSectionsExport = async (): Promise<void> => {
    const exportData = await exportSections();
    if (!exportData) {
      return;
    }

    try {
      // Créer le contenu JSON formaté
      const jsonContent = JSON.stringify(exportData, null, 2);
      
      // Créer un blob avec le contenu JSON
      const blob = new Blob([jsonContent], { type: 'application/json' });
      
      // Créer un nom de fichier avec timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const filename = `sections-export-${timestamp}.json`;
      
      // Créer un lien de téléchargement et le déclencher
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('❌ Erreur lors du téléchargement:', error);
      exportError.value = 'Erreur lors du téléchargement du fichier';
    }
  };

  /**
   * Valide les données avant export
   */
  const validateExportData = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    const sections = sectionsStore.sections;

    if (sections.length === 0) {
      errors.push('Aucune section à exporter');
    }

    // Vérifier que les sections ont des noms valides
    const invalidSections = sections.filter(s => !s.name || s.name.trim() === '');
    if (invalidSections.length > 0) {
      errors.push(`${invalidSections.length} section(s) sans nom valide`);
    }

    // Vérifier les doublons de noms
    const sectionNames = sections.map(s => s.name);
    const duplicates = sectionNames.filter((name, index) => sectionNames.indexOf(name) !== index);
    if (duplicates.length > 0) {
      errors.push(`Noms de sections dupliqués: ${[...new Set(duplicates)].join(', ')}`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  return {
    // État
    exporting,
    exportError,
    
    // Méthodes
    exportSections,
    downloadSectionsExport,
    validateExportData,
    convertSectionToJson,
    generateExportInfo
  };
}; 